import { Icon, IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Button, TextLink, useTooltip } from '@frontend/design-system';
import { FloatingTool } from './floating-tool';

type LinkedStateProps = {
  hasError?: boolean;
  iconName: IconName;
  label: string;
  locationCount?: number;
  onEdit: () => void;
  onRemove?: () => void;
  tooltip?: string;
  type?: 'link' | 'other';
};

// TODO: Consider switching this with an actual chip component
// TODO: Implement the type
export const FloatingDataChip = ({
  hasError = false,
  iconName,
  label,
  locationCount,
  onEdit,
  onRemove,
  tooltip,
  type: _type = 'other',
}: LinkedStateProps) => {
  const { Tooltip, tooltipProps, triggerProps } = useTooltip({ placement: 'top' });
  const showLocationCount = !!locationCount && locationCount > 1;
  const countColor = hasError ? theme.colors.critical50 : theme.colors.neutral50;

  return (
    <FloatingTool
      css={{
        alignItems: 'center',
        boxShadow: theme.shadows.light,
        display: 'flex',
        padding: theme.spacing(0, 0, 0, 1),

        '&:hover, &:focus, &:focus-within': {
          boxShadow: theme.shadows.floating,
          color: theme.colors.black,
          outline: 'none',
        },
      }}
      {...(tooltip ? triggerProps : {})}
    >
      <div
        css={{
          alignItems: 'center',
          display: 'flex',
          minWidth: 0,
        }}
      >
        <Icon name={iconName} css={{ color: theme.colors.neutral70, marginRight: theme.spacing(0.5) }} />
        <TextLink
          onClick={onEdit}
          css={{
            color: theme.colors.neutral70,
            display: 'block',
            marginRight: theme.spacing(1),
            maxWidth: 220,
            overflow: 'hidden',
            textDecoration: 'underline',
            textOverflow: 'ellipsis',
            transition: 'all 250ms ease',
            whiteSpace: 'nowrap',
            ':hover, :focus': { color: theme.colors.primary50 },
          }}
        >
          {label}
        </TextLink>
      </div>
      {showLocationCount && (
        <span
          css={{
            alignItems: 'center',
            alignSelf: 'stretch',
            backgroundColor: hasError ? theme.colors.critical5 : undefined,
            color: countColor,
            columnGap: theme.spacing(0.25),
            display: 'inline-flex',
            fontSize: theme.font.size.small,
            fontWeight: theme.font.weight.bold,
            padding: theme.spacing(0, 0.5),
          }}
        >
          <Icon name='locations-small' css={{ color: countColor }} />
          <span>{locationCount}</span>
        </span>
      )}
      {onRemove && (
        <Button
          iconName='x-small'
          variant='secondary'
          size='small'
          onClick={onRemove}
          css={{
            borderBottomLeftRadius: 0,
            borderLeft: `solid 1px ${theme.colors.neutral20}`,
            borderTopLeftRadius: 0,
            svg: { color: theme.colors.neutral70 },
            ':hover, :focus': { color: theme.colors.neutral10 },
          }}
        />
      )}
      {tooltip && <Tooltip {...tooltipProps}>{tooltip}</Tooltip>}
    </FloatingTool>
  );
};
